"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { useEffect, useState } from "react";

import Accordion from "@/components/Accordion/Accordion";
import AccordionItem from "@/components/Accordion/AccordionItem";
import { FooterProps } from "@/components/Footer/types";
import FooterSocial from "@/components/Social/FooterSocial";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import useBreakPoint from "@/hooks/useBreakpoint";
import { CustomLink } from "@/types";
import { transparentPixel } from "@/utils/string";

import "./footer.css";

export default function Footer({ damacLogo, pageClusters, legalPages, socialChannels, copyright, mobileLabel }: FooterProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { isMd } = useBreakPoint("md");
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(isMd);

  useEffect(() => {
    setIsExpanded(isMd);
  }, [isMd]);

  return (
    <footer className="footer-corporate w-full">
      <div className="m-auto px-m md:container md:mx-auto md:px-3xl 2xl:px-[136px]">
        <div className="footer-corporate__content">
          {/* LOGO */}
          <div className="footer-corporate-logo">
            <div className="flex items-center justify-center h-full md:justify-normal">
              <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
                <Image
                  className="m-auto max-w-[170px] md:m-0 "
                  src={damacLogo?.link?.image?.url || transparentPixel}
                  width={170}
                  height={20}
                  alt={damacLogo?.link?.image?.description || damacLogo?.link?.image?.name || ""}
                />
              </AnchorLink>
            </div>
          </div>
          {/* LINKS ACCORDION*/}
          {/* SOCIAL */}
          <div className="footer-corporate-social-links">
            <p className="text-s mt-m mb-s md:hidden">{mobileLabel}</p>
            <div className="flex flex-row items-center justify-center md:flex-row md:justify-end h-l md:h-full gap-m">
              {socialChannels?.map((social, i) => (
                <FooterSocial key={`footer-corporate-social-${social.id}`} label={social.label} src={`${social.link?.icon}`} url={social.link.url} />
              ))}
            </div>
          </div>
          <div className="footer-corporate-links">
            <div className="w-full only-on-mobile md:flex md:py-l md:justify-between md:gap-3xs">
              {pageClusters?.slice(0).map((item, index) => (
                <Accordion key={`footer-group${index}`} classes="text-s min-w-[200px] md:min-w-[min(calc(20%-8px),_200px)] text-white">
                  <AccordionItem
                    tag="div"
                    title={item.title}
                    innerTag="h3"
                    accordionId={`accordion-${index}`}
                    expanded={isExpanded}
                    useButton={!isExpanded}
                  >
                    <ul className="px-0 pb-0 mb-xs md:pb-m">
                      {item.pages?.map((item, itemIndex) => (
                        <li className="py-m font-secondary-light text-m md:text-s md:py-xxs" key={`footer-pages-${item.id}-${itemIndex}`}>
                          <AnchorLink className="hover:underline underline-offset-4" href={getLink(item, locale).toString()}>
                            {item.label}
                          </AnchorLink>
                        </li>
                      ))}
                    </ul>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
          </div>
          {/* BOTTOM LINKS */}
          <div className="footer-corporate-bottom">
            <div className="footer-corporate-bottom-links">
              {legalPages && (
                <ul className="footer-corporate-bottom-link-items">
                  {legalPages.map((item) => (
                    <li key={`footer-corporate-bottom-link-${item.id}`}>
                      <AnchorLink href={getLink(item, locale).toString()}>{item.label}</AnchorLink>
                    </li>
                  ))}
                </ul>
              )}
              <p>
                {copyright}
                {` `}
                <small>({`v${process.env?.VERSION ?? ""}`})</small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
